import React, { useState, useEffect } from 'react';
import { array, bool, func, object, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { PiCrownSimpleFill } from 'react-icons/pi';
import { RxCrossCircled } from 'react-icons/rx';
import { FaCheckCircle } from 'react-icons/fa';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import {
  H1,
  H2,
  H3,
  H4,
  PrimaryButton,
  SecondaryButton,
  IconCheckmark,
  Page,
  LayoutSingleColumn,
  Modal,
  InlineTextButton,
} from '../../components';
import { IoIosArrowRoundBack } from 'react-icons/io';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './SubscriptionPlan.module.css';
import { apiBaseUrl } from '../../util/api';
import axios from 'axios';
import LoginForm from '../AuthenticationPage/LoginForm/LoginForm';
import { authenticationInProgress, login, signup } from '../../ducks/auth.duck';
import ProviderLoginForm from '../AuthenticationPage/LoginForm/ProviderLoginForm';
import { isSignupEmailTakenError } from '../../util/errors';
import { createResourceLocatorString } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { getUserPlan } from '../../util/getUserPlan';

const FeatureItem = ({ children, highlighted = false, disabled = false }) => (
  <div
    className={classNames(css.featureItem, {
      [css.highlighted]: highlighted,
      [css.disabled]: disabled,
    })}
  >
    {disabled ? (
      <RxCrossCircled className={css.crossIcon} size={18} />
    ) : highlighted ? (
      <PiCrownSimpleFill className={css.crownIcon} size={18} />
    ) : (
      <FaCheckCircle className={css.checkIcon} size={18} />
    )}
    <span className={css.featureText}>{children}</span>
  </div>
);

const PaymentOptionsModal = ({
  isOpen,
  onClose,
  selectedPlan,
  selectedPaymentOption,
  setSelectedPaymentOption,
  onConfirm,
  isLoading,
  intl,
  planData,
  commitmentMonths = 12, // Dynamic commitment length, default to 12
}) => {
  if (!isOpen) return null;

  // Get plan-specific pricing
  const installmentPrice =
    planData?.yearlyPriceInstallment ||
    intl.formatMessage({ id: 'SubscriptionPlan.paymentOptions.installmentPrice' });
  const singlePrice =
    planData?.yearlyPrice ||
    intl.formatMessage({ id: 'SubscriptionPlan.paymentOptions.singlePaymentPrice' });

  // Create dynamic details with actual plan pricing and commitment length
  const installmentTotal = intl.formatMessage(
    { id: 'SubscriptionPlan.paymentOptions.installmentTotalDynamic' },
    { totalPrice: singlePrice }
  );
  const installmentContract = intl.formatMessage(
    { id: 'SubscriptionPlan.paymentOptions.installmentContractDynamic' },
    { months: commitmentMonths }
  );

  const paymentOptions = [
    {
      id: 'installment',
      title: intl.formatMessage({ id: 'SubscriptionPlan.paymentOptions.installmentTitle' }),
      price: installmentPrice,
      details: `${installmentTotal}\n${installmentContract}`,
      isRecommended: true,
    },
    {
      id: 'single',
      title: intl.formatMessage({ id: 'SubscriptionPlan.paymentOptions.singlePaymentTitle' }),
      price: singlePrice,
      details: intl.formatMessage({ id: 'SubscriptionPlan.paymentOptions.singlePaymentDiscount' }),
      isRecommended: false,
    },
  ];

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={css.modal} onClick={e => e.stopPropagation()}>
        <button className={css.closeButton} onClick={onClose}>
          <FormattedMessage id="SubscriptionPlan.close" />
        </button>

        <div className={css.modalHeader}>
          <H3 className={css.modalTitle}>
            <FormattedMessage id="SubscriptionPlan.paymentOptions.title" />
          </H3>
          <p className={css.modalSubtitle}>
            <FormattedMessage id="SubscriptionPlan.paymentOptions.subtitle" />
          </p>
        </div>

        <div className={css.modalContent}>
          <div className={css.paymentOptionsGrid}>
            {paymentOptions.slice(0, 2).map(option => (
              <div
                key={option.id}
                className={classNames(css.paymentOption, {
                  [css.selected]: selectedPaymentOption === option.id,
                })}
                onClick={() => setSelectedPaymentOption(option.id)}
              >
                {option.isRecommended && (
                  <div className={css.recommendedBadge}>
                    <FormattedMessage id="SubscriptionPlan.paymentOptions.recommended" />
                  </div>
                )}
                <h4 className={css.paymentOptionTitle}>{option.title}</h4>
                <p className={css.paymentOptionPrice}>{option.price}</p>
                <p className={css.paymentOptionDetails}>{option.details}</p>
              </div>
            ))}
          </div>

          <div className={css.billingSection}>
            <div className={css.billingRow}>
              <span className={css.billingLabel}>
                <FormattedMessage id="SubscriptionPlan.paymentOptions.dueToday" />
              </span>
              <span className={css.billingAmount}>
                <FormattedMessage id="SubscriptionPlan.paymentOptions.dueAmount" />
              </span>
            </div>
          </div>

          <div className={css.freeTrialSection}>
            <div className={css.freeTrialItem}>
              <IconCheckmark className={css.freeTrialIcon} size="small" />
              <div className={css.freeTrialContent}>
                <p className={css.freeTrialDate}>
                  <FormattedMessage id="SubscriptionPlan.paymentOptions.freeTrialNote" />
                </p>
                <p className={css.freeTrialText}>
                  <FormattedMessage id="SubscriptionPlan.paymentOptions.freeTrialDescription" />
                </p>
              </div>
            </div>

            <div className={css.freeTrialItem}>
              <div className={css.freeTrialIcon}>
                <FormattedMessage id="SubscriptionPlan.notificationIcon" />
              </div>
              <div className={css.freeTrialContent}>
                <p className={css.freeTrialDate}>
                  <FormattedMessage id="SubscriptionPlan.paymentOptions.reminderDate" />
                </p>
                <p className={css.freeTrialText}>
                  <FormattedMessage id="SubscriptionPlan.paymentOptions.reminderText" />
                </p>
              </div>
            </div>

            <div className={css.freeTrialItem}>
              <div className={css.freeTrialIcon}>
                <FormattedMessage id="SubscriptionPlan.premiumIcon" />
              </div>
              <div className={css.freeTrialContent}>
                <p className={css.freeTrialDate}>
                  <FormattedMessage id="SubscriptionPlan.paymentOptions.subscriptionDate" />
                </p>
                <p className={css.freeTrialText}>
                  <FormattedMessage id="SubscriptionPlan.paymentOptions.subscriptionText" />
                </p>
              </div>
            </div>
          </div>

          <div className={css.modalActions}>
            <button className={css.nextButton} onClick={onConfirm} disabled={isLoading}>
              {isLoading ? (
                <FormattedMessage id="SubscriptionPlan.processing" />
              ) : (
                <FormattedMessage id="SubscriptionPlan.paymentOptions.nextButton" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProrationModal = ({
  isOpen,
  onClose,
  onConfirm,
  previewData,
  targetPlanName,
  isLoading,
  intl,
  onManageDisableScrolling,
}) => {
  // Scroll to top when modal opens
  useEffect(() => {
    if (isOpen) {
      // Use setTimeout to ensure DOM is fully rendered
      setTimeout(() => {
        // Find the modal scroll layer and scroll to top
        const modalScrollLayer = document.querySelector(
          '.Modal_scrollLayer__1lxQU, [class*="scrollLayer"]'
        );
        if (modalScrollLayer) {
          modalScrollLayer.scrollTop = 0;
        }
        // Also try to scroll the window to top as a fallback
        window.scrollTo(0, 0);
      }, 100);
    }
  }, [isOpen]);

  if (!isOpen || !previewData) return null;

  // Extract data from the preview response
  const currentPlanType = previewData.currentPlan?.planType || '';
  const currentPlanAmount = previewData.currentPlan?.amountFormatted || '';
  const currentPlanPeriod = previewData.currentPlan?.billingPeriod || '';
  const currentPlanRawAmount = previewData.currentPlan?.amount || '0';

  const newPlanType = previewData.newPlan?.planType || '';
  const newPlanAmount = previewData.newPlan?.amountFormatted || '';
  const newPlanPeriod = previewData.newPlan?.billingPeriod || '';
  const newPlanRawAmount = previewData.newPlan?.amount || '0';

  const prorationInfo = previewData.proration || {};
  const proratedAmount =
    prorationInfo.amountFormatted || prorationInfo.priceDifferenceFormatted || 'N/A';
  const proratedValue = parseFloat(prorationInfo.amount || prorationInfo.priceDifference || '0');
  // console.log('Prorated Value:', prorationInfo);
  // Fix Invalid Date issue
  let nextBillingDate = 'N/A';
  if (prorationInfo.nextBillingDateFormatted) {
    nextBillingDate = prorationInfo.nextBillingDateFormatted;
  } else if (previewData.effectiveDate) {
    const dateObj = new Date(previewData.effectiveDate);
    if (!isNaN(dateObj.getTime())) {
      nextBillingDate = dateObj.toLocaleDateString('pt-BR');
    }
  }

  const explanation = prorationInfo.explanation || '';
  const isUpgrade = prorationInfo.isUpgrade || false;
  const isIntervalChange = prorationInfo.isIntervalChange || false;
  const isTrialSwitch = prorationInfo.explanationNote === 'trial_switch';
  const hasProration = proratedValue > 0;

  // Debug logging
  console.log('=== ProrationModal Debug ===');
  console.log('Proration Info:', prorationInfo);
  console.log('Prorated Amount:', proratedAmount);
  console.log('Prorated Value:', proratedValue);
  console.log('Has Proration:', hasProration);
  console.log('Is Upgrade:', isUpgrade);
  console.log('Is Interval Change:', isIntervalChange);
  console.log('Is Trial Switch:', isTrialSwitch);
  console.log('===========================');

  // Get currency symbol from formatted amount
  const currencySymbol = currentPlanAmount.split(' ')[0] || 'R$';

  return (
    <Modal
      id="ProrationModal"
      isOpen={isOpen}
      onClose={onClose}
      usePortal
      onManageDisableScrolling={onManageDisableScrolling}
    >
      <div className={css.prorationModalContent}>
        <H3 className={css.prorationModalTitle}>
          <FormattedMessage id="SubscriptionPlan.prorationModal.title" />
        </H3>

        <div className={css.prorationDetails}>
          {/* Plan Comparison Cards */}
          <div className={css.planComparisonCards}>
            <div className={css.comparisonCard}>
              <div className={css.comparisonCardHeader}>
                <FormattedMessage id="SubscriptionPlan.prorationModal.currentPlan" />
              </div>
              <div className={css.comparisonCardBody}>
                <div className={css.comparisonPlanName}>{currentPlanType}</div>
                <div className={css.comparisonPlanPrice}>{currentPlanAmount}</div>
                <div className={css.comparisonPlanPeriod}>/{currentPlanPeriod}</div>
              </div>
            </div>

            <div className={css.planCardArrow}>→</div>

            <div className={classNames(css.comparisonCard, css.comparisonCardNew)}>
              <div className={css.comparisonCardHeader}>
                <FormattedMessage id="SubscriptionPlan.prorationModal.newPlan" />
              </div>
              <div className={css.comparisonCardBody}>
                <div className={css.comparisonPlanName}>{newPlanType}</div>
                <div className={css.comparisonPlanPrice}>{newPlanAmount}</div>
                <div className={css.comparisonPlanPeriod}>/{newPlanPeriod}</div>
              </div>
            </div>
          </div>

          {/* Proration Summary */}
          {!isIntervalChange && !isTrialSwitch && hasProration && (
            <div
              className={classNames(css.prorationSummary, {
                [css.prorationUpgrade]: isUpgrade,
                [css.prorationDowngrade]: !isUpgrade,
              })}
            >
              <div className={css.prorationLabel}>
                {isUpgrade ? (
                  <FormattedMessage id="SubscriptionPlan.prorationModal.chargeToday" />
                ) : (
                  <FormattedMessage id="SubscriptionPlan.prorationModal.creditApplied" />
                )}
              </div>
              <div className={css.prorationAmount}>{proratedAmount}</div>
            </div>
          )}

          {/* Price Difference Breakdown */}
          {!isIntervalChange && !isTrialSwitch && hasProration && (
            <div className={css.priceBreakdown}>
              <div className={css.priceBreakdownRow}>
                <span className={css.priceBreakdownLabel}>{currentPlanAmount}</span>
                <span className={css.priceBreakdownArrow}>→</span>
                <span className={css.priceBreakdownLabel}>{newPlanAmount}</span>
              </div>
              <div className={css.priceBreakdownDivider}></div>
              <div className={css.priceBreakdownResult}>
                <span className={css.priceBreakdownText}>
                  {isUpgrade ? (
                    <FormattedMessage id="SubscriptionPlan.prorationModal.effectiveCharge" />
                  ) : (
                    <FormattedMessage id="SubscriptionPlan.prorationModal.effectiveCredit" />
                  )}
                </span>
                <span
                  className={classNames(css.priceBreakdownValue, {
                    [css.priceBreakdownUpgrade]: isUpgrade,
                    [css.priceBreakdownDowngrade]: !isUpgrade,
                  })}
                >
                  {proratedAmount}
                </span>
              </div>
            </div>
          )}

          {/* Next Billing Info */}
          {nextBillingDate && nextBillingDate !== 'N/A' && (
            <div className={css.nextBillingInfo}>
              <div className={css.nextBillingRow}>
                <span className={css.nextBillingLabel}>
                  <FormattedMessage id="SubscriptionPlan.prorationModal.nextBilling" />
                </span>
                <span className={css.nextBillingValue}>{nextBillingDate}</span>
              </div>
            </div>
          )}

          {/* Interval Change Notice */}
          {isIntervalChange && (
            <div className={css.intervalChangeNotice}>
              <FormattedMessage id="SubscriptionPlan.prorationModal.intervalChangeExplanation" />
            </div>
          )}
        </div>

        <div className={css.prorationModalActions}>
          <SecondaryButton className={css.prorationModalButton} onClick={onClose}>
            <FormattedMessage id="SubscriptionPlan.prorationModal.cancel" />
          </SecondaryButton>
          <PrimaryButton
            className={css.prorationModalButton}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? (
              <FormattedMessage id="SubscriptionPlan.processing" />
            ) : (
              <FormattedMessage id="SubscriptionPlan.prorationModal.confirm" />
            )}
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
};

const PlanCard = ({
  id,
  title,
  monthlyPrice,
  yearlyPrice,
  yearlyPriceInstallment,
  subtitle,
  features,
  buttonText,
  buttonAction,
  isPopular = false,
  isCurrent = false,
  isYearly = false,
  isLoading = false,
  showInstallmentPrice = false,
  intl,
}) => {
  const cardClasses = classNames(css.planCard, {
    [css.popularCard]: isPopular,
    [css.currentCard]: isCurrent,
  });

  // Determine which price to display based on billing toggle
  let displayPrice = null;
  let priceSubtext = null;

  if (monthlyPrice && yearlyPrice) {
    if (isYearly) {
      // For yearly billing, show the yearly price broken down per month
      displayPrice = yearlyPriceInstallment || yearlyPrice;
      priceSubtext = intl.formatMessage({ id: 'SubscriptionPlan.chargedAnnually' });
    } else {
      displayPrice = monthlyPrice;
      priceSubtext = intl.formatMessage({ id: 'SubscriptionPlan.chargedMonthly' });
    }
  }

  return (
    <div className={cardClasses}>
      {isPopular && (
        <div className={css.popularBadge}>
          <FormattedMessage id="SubscriptionPlan.mostPopular" />
        </div>
      )}

      <div className={css.planHeader}>
        {displayPrice ? (
          <>
            <div className={css.priceWrapper}>
              <span className={css.price}>{displayPrice.split(' /')[0]}</span>
              {displayPrice.includes(' /') && (
                <span className={css.priceUnit}> / {displayPrice.split(' /')[1]}</span>
              )}
            </div>
            {priceSubtext && <div className={css.priceSubtitle}>{priceSubtext}</div>}
            <H3 className={css.planTitle}>{title}</H3>
          </>
        ) : (
          <>
            <div className={css.priceWrapper}>
              <span className={css.price}>
                <FormattedMessage id="SubscriptionPlan.freePlan.price" />
              </span>
            </div>
            <H3 className={css.planTitle}>{title}</H3>
          </>
        )}
        {subtitle && <div className={css.planSubtitle}>{subtitle}</div>}
      </div>

      <div className={css.featuresContainer}>
        {features.map((feature, index) => (
          <FeatureItem key={index} highlighted={feature.highlighted} disabled={feature.disabled}>
            {feature.text}
          </FeatureItem>
        ))}
        {id !== 'free' && (
          <div className={css.info}>
            {intl.formatMessage({ id: 'SubscriptionPlan.customUrlZeroFee' })}
          </div>
        )}
      </div>

      <div className={css.buttonContainer}>
        {isCurrent ? (
          <SecondaryButton className={css.planButton} disabled>
            {buttonText}
          </SecondaryButton>
        ) : (
          <PrimaryButton
            className={css.planButton}
            onClick={buttonAction}
            disabled={!buttonAction || isLoading}
          >
            {isLoading ? <FormattedMessage id="SubscriptionPlan.processing" /> : buttonText}
          </PrimaryButton>
        )}
      </div>
    </div>
  );
};

const SubscriptionPlanComponent = props => {
  const {
    intl,
    currentUser,
    scrollingDisabled,
    currentPlan = 'free',
    onManageDisableScrolling,
    isAuthenticated,
    submitLogin,
    submitSignup,
    loginError,
    signupError,
    authInProgress,
    history,
  } = props;
  const routeConfiguration = useRouteConfiguration();
  const [isYearly, setIsYearly] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPlanId, setLoadingPlanId] = useState(null); // Track which specific plan is loading
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPaymentOption, setSelectedPaymentOption] = useState('installment');

  // Proration modal states
  const [showProrationModal, setShowProrationModal] = useState(false);
  const [prorationPreview, setProrationPreview] = useState(null);
  const [targetSwitchPlan, setTargetSwitchPlan] = useState(null);

  // Modal states
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', message: '' });

  const [showAuthenticationModal, setShowAuthenticationModal] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [showUserTypeError, setShowUserTypeError] = useState(false);

  useEffect(() => {
    setShowAuthenticationModal(false);
  }, [isAuthenticated]);

  const schemaTitle = intl.formatMessage({ id: 'SubscriptionPlan.pageTitle' });
  const schemaDescription = intl.formatMessage({ id: 'SubscriptionPlan.pageDescription' });

  // Get trial configuration from environment
  const trialsEnabled = process.env.REACT_APP_ENABLE_TRIALS === 'true';
  const trialPeriodDays = parseInt(process.env.REACT_APP_TRIAL_PERIOD_DAYS) || 30;

  const signupErrorMessage = (
    <div className={css.error}>
      {isSignupEmailTakenError(signupError) ? (
        <FormattedMessage id="AuthenticationPage.signupFailedEmailAlreadyTaken" />
      ) : (
        <FormattedMessage id="AuthenticationPage.signupFailed" />
      )}
    </div>
  );

  const loginErrorMessage = (
    <div className={css.error}>
      <FormattedMessage id="AuthenticationPage.loginFailed" />
    </div>
  );

  const loginOrSignupError =
    isLogin && !!loginError ? loginErrorMessage : !!signupError ? signupErrorMessage : null;

  const handleLoginSubmit = val => {
    const { email, password } = val;
    submitLogin({ email, password }).then(() => {
      // setShowAuthenticationModal(false);
    });
  };

  // Modal helper functions
  const showErrorDialog = (title, message) => {
    setModalContent({ title, message });
    setShowErrorModal(true);
  };

  const showSuccessDialog = (title, message) => {
    setModalContent({ title, message });
    setShowSuccessModal(true);
  };

  const showConfirmDialog = (title, message, onConfirm) => {
    setModalContent({ title, message, onConfirm });
    setShowConfirmModal(true);
  };

  // Extract current plan from user metadata and private data
  const subscription = currentUser?.attributes?.profile?.privateData?.subscription;
  const metadata = currentUser?.attributes?.profile?.metadata;

  // Check if subscription is expired
  const validTill = metadata?.subscription?.validTill;
  const isExpired = validTill ? new Date() > new Date(validTill) : false;

  const userSubscriptionPlan = subscription?.planType || metadata?.subscriptionPlan || 'free';
  const userBillingPeriod = subscription?.billingPeriod || 'monthly';
  const userUniquePlanId = subscription?.uniquePlanId || userSubscriptionPlan;
  const userIsInstallment = subscription?.isInstallment || false;
  // User is only considered to have an active subscription if they're on a paid plan
  // Free plan doesn't count as an active subscription even if flag is true
  // Also check if subscription is expired
  const isSubscriptionActive =
    (subscription?.isActive || metadata?.subscriptionActive || false) &&
    userSubscriptionPlan !== 'free' &&
    !isExpired;

  // Installment tracking data
  const installmentsPaid = subscription?.installmentsPaid || 0;
  const totalInstallmentsRequired = subscription?.totalInstallmentsRequired || 12;
  const remainingInstallments = subscription?.remainingInstallments || totalInstallmentsRequired;
  const cancelRequested = subscription?.cancelRequested || false;

  // Dual subscription tracking data
  const legacySubscriptions = subscription?.legacySubscriptions || [];
  const hasPendingCancellations = metadata?.hasPendingCancellations || false;
  const legacyCharges = metadata?.subscription?.legacyCharges;

  // Check if user has ever had a subscription (trial eligibility)
  const hasEverHadSubscription =
    subscription?.stripeCustomerId ||
    subscription?.hasTrial ||
    (userSubscriptionPlan !== 'free' && isSubscriptionActive);
  const isTrialEligible = !hasEverHadSubscription;

  // Helper function to determine if a plan is higher than current
  const planHierarchy = {
    free: 0,
    'premium-monthly': 1,
    'premium-yearly-single': 1,
    'premium-yearly-installment': 1,
    'pro-monthly': 2,
    'pro-yearly-single': 2,
    'pro-yearly-installment': 2,
  };

  // Generate current plan unique identifier based on current billing context
  const getCurrentUniquePlanId = (planType, billingContext) => {
    if (planType === 'premium') {
      if (billingContext === 'yearly') {
        // Default to installment for yearly unless specifically single payment
        return userIsInstallment ? 'premium-yearly-installment' : 'premium-yearly-single';
      }
      return 'premium-monthly';
    } else if (planType === 'pro') {
      if (billingContext === 'yearly') {
        // Default to installment for yearly unless specifically single payment
        return userIsInstallment ? 'pro-yearly-installment' : 'pro-yearly-single';
      }
      return 'pro-monthly';
    }
    return 'free';
  };

  const currentPlanKey = userUniquePlanId || userSubscriptionPlan;
  const isHigherPlan = planId => planHierarchy[planId] > planHierarchy[currentPlanKey];

  const isCurrentPlan = planId => {
    // Defensive check: log what we're checking
    const debugInfo = {
      planId,
      isSubscriptionActive,
      userSubscriptionPlan,
      userBillingPeriod,
      isYearly,
    };

    // If no subscription is active, only free is current
    if (!isSubscriptionActive) {
      console.log(
        `isCurrentPlan(${planId}): No active subscription, returning ${planId === 'free'}`,
        debugInfo
      );
      return planId === 'free';
    }

    // If there's an active subscription, free is never current
    if (planId === 'free') {
      console.log(
        `isCurrentPlan(${planId}): Active subscription exists, free is NOT current`,
        debugInfo
      );
      return false;
    }

    // For premium/pro plans, check if user has that specific plan type
    const hasExactPlan =
      (planId === 'premium' && userSubscriptionPlan === 'premium') ||
      (planId === 'pro' && userSubscriptionPlan === 'pro');

    if (!hasExactPlan) {
      console.log(`isCurrentPlan(${planId}): Plan type mismatch, returning false`, debugInfo);
      return false;
    }

    // Check billing period alignment
    // User's actual billing period vs what's being displayed
    const userIsYearly =
      userBillingPeriod === 'yearly' ||
      userBillingPeriod === 'yearly-installment' ||
      userBillingPeriod === 'yearly-single';

    const result = userIsYearly === isYearly;
    console.log(
      `isCurrentPlan(${planId}): userIsYearly=${userIsYearly}, isYearly=${isYearly}, result=${result}`,
      debugInfo
    );

    // Show as current if billing periods match the toggle
    return result;
  };

  // Helper function to get button text
  const getButtonText = (planId, originalButtonText) => {
    // Check if this plan is current and return appropriate text
    const isCurrent = isCurrentPlan(planId);

    // Always show "Current plan" for whichever plan is actually current
    if (isCurrent) {
      return intl.formatMessage({ id: 'SubscriptionPlan.currentPlan' });
    }

    // For free plan when NOT current (user has active paid subscription)
    if (planId === 'free') {
      // If user has active subscription, they can't "select" free - they need to cancel first
      if (isSubscriptionActive) {
        return intl.formatMessage({ id: 'SubscriptionPlan.freePlan.cancelToDowngrade' });
      }
      // If no subscription, use the original button text ("Current plan")
      return originalButtonText;
    }

    // For PRO plan, check trial eligibility
    if (planId === 'pro') {
      if (isTrialEligible && trialsEnabled) {
        // Fresh user - show trial option (both monthly and yearly)
        return intl.formatMessage(
          { id: 'SubscriptionPlan.freeTrialButton' },
          { days: trialPeriodDays }
        );
      } else if (!isTrialEligible && !isYearly) {
        // User has had subscription before - no trial available
        const upgradeKey = 'SubscriptionPlan.upgradeToProMonthly';
        return intl.formatMessage({ id: upgradeKey });
      }

      // Check if user already has premium and wants to upgrade to pro
      if (userSubscriptionPlan === 'premium') {
        const upgradeKey = isYearly
          ? 'SubscriptionPlan.upgradeToProAnnual'
          : 'SubscriptionPlan.upgradeToProMonthly';
        return intl.formatMessage({ id: upgradeKey });
      }

      // Check if user has same plan type but different billing period
      if (userSubscriptionPlan === 'pro') {
        const userIsOnYearlyPlan = userBillingPeriod !== 'monthly';
        const targetIsYearly = isYearly;

        if (userIsOnYearlyPlan !== targetIsYearly) {
          const switchKey = isYearly
            ? 'SubscriptionPlan.switchToProAnnual'
            : 'SubscriptionPlan.switchToProMonthly';
          return intl.formatMessage({ id: switchKey });
        }
      }

      // Default upgrade text for pro
      const upgradeKey = isYearly
        ? 'SubscriptionPlan.upgradeToProAnnual'
        : 'SubscriptionPlan.upgradeToProMonthly';
      return intl.formatMessage({ id: upgradeKey });
    }

    // For Premium plan
    if (planId === 'premium') {
      // For fresh users (trial eligible), show trial button
      if (isTrialEligible && trialsEnabled) {
        return intl.formatMessage(
          { id: 'SubscriptionPlan.freeTrialButton' },
          { days: trialPeriodDays }
        );
      } else if (!isTrialEligible && !isYearly) {
        // User has had subscription before - no trial available
        return intl.formatMessage({ id: 'SubscriptionPlan.upgradeToPremium' });
      }

      // Check if user has same plan type but different billing period
      if (userSubscriptionPlan === 'premium') {
        const userIsOnYearlyPlan = userBillingPeriod !== 'monthly';
        const targetIsYearly = isYearly;

        if (userIsOnYearlyPlan !== targetIsYearly) {
          const switchKey = isYearly
            ? 'SubscriptionPlan.switchToPremiumAnnual'
            : 'SubscriptionPlan.switchToPremiumMonthly';
          return intl.formatMessage({ id: switchKey });
        }
      }

      // Default upgrade text for premium
      return intl.formatMessage({ id: 'SubscriptionPlan.upgradeToPremium' });
    }

    return originalButtonText;
  };

  // Plan data
  const plans = [
    {
      id: 'free',
      title: intl.formatMessage({ id: 'SubscriptionPlan.freePlan.title' }),
      monthlyPrice: null,
      yearlyPrice: null,
      subtitle: intl.formatMessage({ id: 'SubscriptionPlan.freePlan.subtitle' }),
      buttonText: getButtonText(
        'free',
        intl.formatMessage({ id: 'SubscriptionPlan.freePlan.button' })
      ),
      features: [
        { text: intl.formatMessage({ id: 'SubscriptionPlan.freePlan.feature1' }), disabled: false },
        { text: intl.formatMessage({ id: 'SubscriptionPlan.freePlan.feature2' }), disabled: false },
        { text: intl.formatMessage({ id: 'SubscriptionPlan.freePlan.feature3' }), disabled: false },
        { text: intl.formatMessage({ id: 'SubscriptionPlan.freePlan.feature4' }), disabled: false },
        { text: intl.formatMessage({ id: 'SubscriptionPlan.freePlan.feature5' }), disabled: false },
        { text: intl.formatMessage({ id: 'SubscriptionPlan.freePlan.feature6' }), disabled: false },
        { text: intl.formatMessage({ id: 'SubscriptionPlan.freePlan.feature7' }), disabled: true },
        { text: intl.formatMessage({ id: 'SubscriptionPlan.freePlan.feature8' }), disabled: true },
        { text: intl.formatMessage({ id: 'SubscriptionPlan.freePlan.feature9' }), disabled: true },
        { text: intl.formatMessage({ id: 'SubscriptionPlan.freePlan.feature10' }), disabled: true },
      ],
      isPopular: false,
      isCurrent: isCurrentPlan('free'),
    },
    {
      id: 'premium',
      title: intl.formatMessage({ id: 'SubscriptionPlan.premiumPlan.title' }),
      monthlyPrice: intl.formatMessage({ id: 'SubscriptionPlan.premiumPlan.priceMonthly' }),
      yearlyPrice: intl.formatMessage({ id: 'SubscriptionPlan.premiumPlan.priceYearly' }),
      yearlyPriceInstallment: intl.formatMessage({
        id: 'SubscriptionPlan.premiumPlan.priceYearlyInstallment',
      }),
      subtitle: intl.formatMessage({ id: 'SubscriptionPlan.premiumPlan.subtitle' }),
      buttonText: getButtonText(
        'premium',
        intl.formatMessage({ id: 'SubscriptionPlan.premiumPlan.button' })
      ),
      features: [
        {
          text: intl.formatMessage({ id: 'SubscriptionPlan.premiumPlan.feature1' }),
          disabled: false,
        },
        {
          text: intl.formatMessage({ id: 'SubscriptionPlan.premiumPlan.feature2' }),
          disabled: false,
        },
        {
          text: intl.formatMessage({ id: 'SubscriptionPlan.premiumPlan.feature3' }),
          disabled: false,
        },
        {
          text: intl.formatMessage({ id: 'SubscriptionPlan.premiumPlan.feature4' }),
          disabled: false,
        },
        {
          text: intl.formatMessage({ id: 'SubscriptionPlan.premiumPlan.feature5' }),
          disabled: false,
        },
        {
          text: intl.formatMessage({ id: 'SubscriptionPlan.premiumPlan.feature6' }),
          // highlighted: true,
          disabled: false,
        },
        {
          text: intl.formatMessage({ id: 'SubscriptionPlan.premiumPlan.feature7' }),
          highlighted: true,
          disabled: false,
        },
        {
          text: intl.formatMessage({ id: 'SubscriptionPlan.premiumPlan.feature8' }),
          highlighted: true,
          disabled: false,
        },
        {
          text: intl.formatMessage({ id: 'SubscriptionPlan.premiumPlan.feature9' }),
          disabled: true,
        },
        {
          text: intl.formatMessage({ id: 'SubscriptionPlan.premiumPlan.feature10' }),
          disabled: true,
        },
      ],
      isPopular: true,
      isCurrent: isCurrentPlan('premium'),
    },
    {
      id: 'pro',
      title: intl.formatMessage({ id: 'SubscriptionPlan.proPlan.title' }),
      monthlyPrice: intl.formatMessage({ id: 'SubscriptionPlan.proPlan.priceMonthly' }),
      yearlyPrice: intl.formatMessage({ id: 'SubscriptionPlan.proPlan.priceYearly' }),
      yearlyPriceInstallment: intl.formatMessage({
        id: 'SubscriptionPlan.proPlan.priceYearlyInstallment',
      }),
      subtitle: intl.formatMessage({ id: 'SubscriptionPlan.proPlan.subtitle' }),
      buttonText: getButtonText(
        'pro',
        intl.formatMessage({ id: 'SubscriptionPlan.proPlan.button' })
      ),
      features: [
        { text: intl.formatMessage({ id: 'SubscriptionPlan.proPlan.feature1' }), disabled: false },
        { text: intl.formatMessage({ id: 'SubscriptionPlan.proPlan.feature2' }), disabled: false },
        { text: intl.formatMessage({ id: 'SubscriptionPlan.proPlan.feature3' }), disabled: false },
        { text: intl.formatMessage({ id: 'SubscriptionPlan.proPlan.feature4' }), disabled: false },
        { text: intl.formatMessage({ id: 'SubscriptionPlan.proPlan.feature5' }), disabled: false },
        {
          text: intl.formatMessage({ id: 'SubscriptionPlan.proPlan.feature6' }),
          // highlighted: true,
          disabled: false,
        },
        {
          text: intl.formatMessage({ id: 'SubscriptionPlan.proPlan.feature7' }),
          highlighted: true,
          disabled: false,
        },
        {
          text: intl.formatMessage({ id: 'SubscriptionPlan.proPlan.feature8' }),
          highlighted: true,
          disabled: false,
        },
        {
          text: intl.formatMessage({ id: 'SubscriptionPlan.proPlan.feature9' }),
          highlighted: true,
          disabled: false,
        },
        {
          text: intl.formatMessage({ id: 'SubscriptionPlan.proPlan.feature10' }),
          highlighted: true,
          disabled: false,
        },
      ],
      isPopular: false,
      isCurrent: isCurrentPlan('pro'),
    },
  ];

  // Create secure checkout session
  const createCheckoutSession = async (planType, billingPeriod) => {
    try {
      setIsLoading(true);
      setLoadingPlanId(planType); // Set which plan is loading
      if (!isAuthenticated) {
        setShowAuthenticationModal(true);
        return;
      }

      if (
        currentUser?.attributes?.profile?.publicData?.userType === 'cliente-id' ||
        currentUser?.attributes?.profile?.publicData?.userType === 'cliente'
      ) {
        setShowUserTypeError(true);
        return;
      }

      const response = await axios.post(
        `${apiBaseUrl()}/api/subscription/create-checkout-session`,
        {
          planType,
          billingPeriod,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
          withCredentials: true, // Include authentication cookies
        }
      );

      const data = response.data;

      if (response.status !== 200) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      showErrorDialog(
        intl.formatMessage({ id: 'SubscriptionPlan.paymentErrorTitle' }),
        `${intl.formatMessage({ id: 'SubscriptionPlan.paymentErrorMessage' })}: ${error.message}`
      );
    } finally {
      setIsLoading(false);
      setLoadingPlanId(null); // Clear loading plan
    }
  };

  // Preview subscription switch to show proration details
  const previewSubscriptionSwitch = async (targetPlan, targetBillingPeriod) => {
    try {
      setIsLoading(true);
      setLoadingPlanId(targetPlan); // Set which plan is loading

      console.log('=== Preview Subscription Switch ===');
      console.log('Target Plan:', targetPlan);
      console.log('Target Billing:', targetBillingPeriod);
      console.log('Current Subscription Active:', isSubscriptionActive);

      const response = await axios.post(
        `${apiBaseUrl()}/api/subscription/preview-switch`,
        {
          planType: targetPlan,
          billingPeriod: targetBillingPeriod,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
          withCredentials: true,
        }
      );

      console.log('Preview Response:', response.data);

      if (response.status === 200 && response.data.success) {
        // If subscription is canceled, skip preview and go directly to switch
        if (response.data.isCanceled) {
          console.log('Subscription is canceled - skipping preview, proceeding to switch');
          setIsLoading(false);
          // Directly call switch which will redirect to checkout
          switchSubscription(targetPlan, targetBillingPeriod);
          return;
        }

        // For active subscriptions, show proration preview modal
        setProrationPreview(response.data);
        setTargetSwitchPlan({ planType: targetPlan, billingPeriod: targetBillingPeriod });
        setShowProrationModal(true);
      } else {
        throw new Error(response.data.error || 'Failed to preview subscription switch');
      }
    } catch (error) {
      console.error('Error previewing subscription switch:', error);

      // Check if it's the yearly-to-monthly downgrade error
      const errorKey = error.response?.data?.error;
      const errorMessage =
        errorKey && intl.messages[errorKey]
          ? intl.formatMessage({ id: errorKey })
          : error.response?.data?.error ||
            error.message ||
            intl.formatMessage({ id: 'SubscriptionPlan.previewErrorMessage' });

      showErrorDialog(
        intl.formatMessage({ id: 'SubscriptionPlan.previewErrorTitle' }),
        errorMessage
      );
    } finally {
      setIsLoading(false);
      setLoadingPlanId(null); // Clear loading plan
    }
  };

  // Execute subscription switch
  const switchSubscription = async (planType, billingPeriod) => {
    // Use parameters if provided, otherwise use state
    const targetPlan = planType || targetSwitchPlan?.planType;
    const targetBilling = billingPeriod || targetSwitchPlan?.billingPeriod;

    if (!targetPlan || !targetBilling) return;

    try {
      setIsLoading(true);
      setShowProrationModal(false);

      console.log('=== Executing Subscription Switch ===');
      console.log('Target Plan:', targetPlan);
      console.log('Target Billing:', targetBilling);

      const response = await axios.post(
        `${apiBaseUrl()}/api/subscription/switch`,
        {
          planType: targetPlan,
          billingPeriod: targetBilling,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
          withCredentials: true,
        }
      );

      console.log('Switch Response:', response.data);

      if (response.status === 200 && response.data.success) {
        // Check if requires payment (upgrade via Stripe Checkout)
        if (response.data.requiresPayment && response.data.checkoutUrl) {
          console.log('Redirecting to Stripe Checkout:', response.data.checkoutUrl);
          window.location.href = response.data.checkoutUrl;
        } else {
          // Immediate switch (downgrade or interval change)
          // Check if response has messageKey for translation
          const messageKey = response.data.messageKey;
          const successMessage =
            messageKey && intl.messages[messageKey]
              ? intl.formatMessage({ id: messageKey }, response.data.messageData || {})
              : response.data.message ||
                intl.formatMessage({ id: 'SubscriptionPlan.switchSuccessMessage' });

          showSuccessDialog(
            intl.formatMessage({ id: 'SubscriptionPlan.switchSuccessTitle' }),
            successMessage
          );

          // Reload page after a delay to fetch updated subscription data
          setTimeout(() => {
            window.location.reload();
          }, 5000);
        }
      } else {
        throw new Error(response.data.error || 'Failed to switch subscription');
      }
    } catch (error) {
      console.error('Error switching subscription:', error);

      // Check if it's the yearly-to-monthly downgrade error
      const errorKey = error.response?.data?.error;
      const errorMessage =
        errorKey && intl.messages[errorKey]
          ? intl.formatMessage({ id: errorKey })
          : error.response?.data?.error ||
            error.message ||
            intl.formatMessage({ id: 'SubscriptionPlan.switchErrorMessage' });

      showErrorDialog(
        intl.formatMessage({ id: 'SubscriptionPlan.switchErrorTitle' }),
        errorMessage
      );
    } finally {
      setIsLoading(false);
      setLoadingPlanId(null); // Clear loading plan
      setTargetSwitchPlan(null);
      setProrationPreview(null);
    }
  };

  // Handle Stripe Checkout return (success or cancel)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const upgradeSuccess = urlParams.get('upgrade_success');
    const upgradeCancelled = urlParams.get('upgrade_cancelled');

    if (upgradeSuccess === 'true') {
      console.log('Upgrade completed successfully via Stripe Checkout');
      showSuccessDialog(
        intl.formatMessage({ id: 'SubscriptionPlan.upgradeSuccessTitle' }),
        intl.formatMessage({ id: 'SubscriptionPlan.upgradeSuccessMessage' })
      );

      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);

      // Reload to get fresh data
      setTimeout(() => {
        window.location.reload();
      }, 5000);
    } else if (upgradeCancelled === 'true') {
      console.log('Upgrade cancelled by user');
      showErrorDialog(
        intl.formatMessage({ id: 'SubscriptionPlan.upgradeCancelledTitle' }),
        intl.formatMessage({ id: 'SubscriptionPlan.upgradeCancelledMessage' })
      );

      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [intl]);

  console.log('=== Subscription Plan Debug ===');
  console.log('User Plan:', userSubscriptionPlan);
  console.log('Billing Period:', userBillingPeriod);
  console.log('Active:', isSubscriptionActive);
  console.log('Toggle Yearly:', isYearly);
  console.log('Trial Eligible:', isTrialEligible);
  console.log(
    'Current Plans - Free:',
    isCurrentPlan('free'),
    'Premium:',
    isCurrentPlan('premium'),
    'Pro:',
    isCurrentPlan('pro')
  );
  console.log('================================');

  const handlePlanSelection = planId => {
    console.log('=== Plan Selection ===');
    console.log('Selected Plan:', planId);
    console.log('Is Yearly:', isYearly);
    console.log('Current Subscription Active:', isSubscriptionActive);
    console.log('Current Plan:', userSubscriptionPlan);
    console.log('Current Billing Period:', userBillingPeriod);

    // Handle plan selection logic here
    if (planId === 'free') {
      // Free plan doesn't require payment
      console.log('Free plan selected - no payment required');
      return;
    }

    // Determine target billing period
    const targetBillingPeriod = isYearly ? 'yearly' : 'monthly';

    // Check if user already has an active subscription
    if (isSubscriptionActive && userSubscriptionPlan !== 'free') {
      console.log('User has active subscription - checking for plan switch');

      // Check if switching to the exact same plan
      const targetPlanKey = `${planId}-${targetBillingPeriod}`;
      const currentPlanKey = `${userSubscriptionPlan}-${
        userBillingPeriod === 'monthly' ? 'monthly' : 'yearly'
      }`;

      console.log('Target Plan Key:', targetPlanKey);
      console.log('Current Plan Key:', currentPlanKey);

      if (targetPlanKey === currentPlanKey) {
        console.log('Already on this exact plan - no action needed');
        showErrorDialog(
          intl.formatMessage({ id: 'SubscriptionPlan.alreadySubscribedTitle' }),
          intl.formatMessage({ id: 'SubscriptionPlan.alreadySubscribedMessage' })
        );
        return;
      }

      // If switching to yearly, show payment options modal first
      if (isYearly && (planId === 'premium' || planId === 'pro')) {
        console.log('Switching to yearly - showing payment options modal');
        setSelectedPlan(planId);
        setShowPaymentModal(true);
        return;
      }

      // For monthly switches, show proration preview directly
      console.log('Switching plan - showing proration preview');
      previewSubscriptionSwitch(planId, targetBillingPeriod);
      return;
    }

    // For new subscriptions (no active subscription)
    // If yearly billing is selected, show payment options modal
    if (isYearly && (planId === 'premium' || planId === 'pro')) {
      setSelectedPlan(planId);
      setShowPaymentModal(true);
      return;
    }

    // For monthly billing, proceed directly to checkout
    if (planId === 'premium' || planId === 'pro') {
      createCheckoutSession(planId, targetBillingPeriod);
    } else {
      console.log(`Unknown plan: ${planId}`);
    }
  };

  const handlePaymentConfirm = () => {
    if (!selectedPlan || !selectedPaymentOption) return;

    let billingPeriod;
    let canCancel = true;

    switch (selectedPaymentOption) {
      case 'installment':
        billingPeriod = 'yearly-installment';
        canCancel = false; // Cannot cancel during 12-month commitment
        break;
      case 'single':
        billingPeriod = 'yearly-single';
        canCancel = true; // Can cancel but no refund
        break;
      case 'monthly':
        billingPeriod = 'monthly';
        canCancel = true;
        break;
      default:
        return;
    }

    // Store cancellation policy in session storage for later reference
    sessionStorage.setItem('subscriptionCanCancel', canCancel.toString());
    sessionStorage.setItem('subscriptionType', selectedPaymentOption);

    setShowPaymentModal(false);

    // Check if user has active subscription - if so, show proration preview
    if (isSubscriptionActive && userSubscriptionPlan !== 'free') {
      console.log(
        'Has active subscription - showing proration preview after payment option selection'
      );
      previewSubscriptionSwitch(selectedPlan, billingPeriod);
    } else {
      // New subscription - go directly to checkout
      console.log('New subscription - proceeding to checkout');
      createCheckoutSession(selectedPlan, billingPeriod);
    }
  };
  const handleBack = e => {
    e.preventDefault();
    if (history.length > 1) {
      history.goBack();
      return;
    }
    if (history?.location?.state?.from === 'newListingpage') {
      const url = createResourceLocatorString('NewListingPage', routeConfiguration, {}, {});
      history.replace(url);
      return;
    }
    // history.goBack();
    return;
  };
  const { hasActivePlan, plan } = getUserPlan(currentUser);

  const contactLink = (
    <a href={'https://wa.me/5511976976506'} target="_blank">
      <FormattedMessage id="SubscriptionPlan.linkText" />
    </a>
  );

  return (
    <Page
      title={schemaTitle}
      description={schemaDescription}
      scrollingDisabled={scrollingDisabled}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: schemaDescription,
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn>
        <TopbarContainer />

        <div className={css.root}>
          <div className={css.container}>
            <InlineTextButton onClick={handleBack} type="button" className={css.backBtn}>
              <IoIosArrowRoundBack size={24} />
              <FormattedMessage id="SubscriptionPlan.goBackBtn" />
              {/* {intl.formatMessage({ id: 'back' })} */}
            </InlineTextButton>
            <div className={css.header}>
              <div className={css.discountBanner}>
                <FormattedMessage id="SubscriptionPlan.annualDiscount" />
              </div>

              {/* Monthly/Yearly Toggle */}
              <div className={css.toggleContainer}>
                <div className={classNames(css.billingToggle, { [css.yearly]: isYearly })}>
                  <button
                    className={classNames(css.toggleOption, { [css.active]: !isYearly })}
                    onClick={() => setIsYearly(false)}
                  >
                    <FormattedMessage id="SubscriptionPlan.monthly" />
                  </button>
                  <button
                    className={classNames(css.toggleOption, { [css.active]: isYearly })}
                    onClick={() => setIsYearly(true)}
                  >
                    <FormattedMessage id="SubscriptionPlan.annual" />
                  </button>
                </div>
              </div>
            </div>

            {/* Dual Subscription Banner */}
            {hasPendingCancellations && legacyCharges && (
              <div className={css.dualSubscriptionBanner}>
                <div className={css.dualSubscriptionContent}>
                  <div className={css.dualSubscriptionHeader}>
                    <h4 className={css.dualSubscriptionTitle}>
                      <FormattedMessage id="SubscriptionPlan.dualSubscription.title" />
                    </h4>
                  </div>
                  <p className={css.dualSubscriptionDescription}>
                    <FormattedMessage id="SubscriptionPlan.dualSubscription.description" />
                  </p>
                  <div className={css.dualSubscriptionDetails}>
                    <p className={css.dualSubscriptionAmount}>
                      <FormattedMessage
                        id="SubscriptionPlan.dualSubscription.amount"
                        values={{ amount: `R$${legacyCharges.totalMonthlyCommitments}` }}
                      />
                    </p>
                    <p className={css.dualSubscriptionEndDate}>
                      <FormattedMessage
                        id="SubscriptionPlan.dualSubscription.endsOn"
                        values={{
                          date: new Date(legacyCharges.commitmentEndDate).toLocaleDateString(),
                        }}
                      />
                    </p>
                    <p className={css.dualSubscriptionNote}>
                      <FormattedMessage
                        id="SubscriptionPlan.dualSubscription.description2"
                        values={{ description: legacyCharges.description }}
                      />
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Installment Progress Banner */}
            {/* {userIsInstallment && isSubscriptionActive && (
              <div className={css.installmentBanner}>
                <div className={css.installmentProgress}>
                  <div className={css.progressHeader}>
                    <h4 className={css.progressTitle}>
                      <FormattedMessage id="SubscriptionPlan.installmentProgress.title" />
                    </h4>
                    {cancelRequested && (
                      <span className={css.cancelRequestedBadge}>
                        <FormattedMessage id="SubscriptionPlan.installmentProgress.cancelRequested" />
                      </span>
                    )}
                  </div>
                  <div className={css.progressBar}>
                    <div
                      className={css.progressFill}
                      style={{ width: `${(installmentsPaid / totalInstallmentsRequired) * 100}%` }}
                    />
                  </div>
                  <p className={css.progressText}>
                    <FormattedMessage
                      id="SubscriptionPlan.installmentProgress.status"
                      values={{
                        paid: installmentsPaid,
                        total: totalInstallmentsRequired,
                        remaining: remainingInstallments,
                      }}
                    />
                  </p>
                  {cancelRequested && (
                    <p className={css.progressNote}>
                      <FormattedMessage
                        id="SubscriptionPlan.installmentProgress.cancelNote"
                        values={{ remaining: remainingInstallments }}
                      />
                    </p>
                  )}
                </div>
              </div>
            )} */}

            <div className={css.plansContainer}>
              {plans.map(plan => (
                <PlanCard
                  id={plan.id}
                  key={plan.id}
                  title={plan.title}
                  monthlyPrice={plan.monthlyPrice}
                  yearlyPrice={plan.yearlyPrice}
                  yearlyPriceInstallment={plan.yearlyPriceInstallment}
                  subtitle={plan.subtitle}
                  features={plan.features}
                  buttonText={plan.buttonText}
                  buttonAction={
                    plan.isCurrent
                      ? null
                      : plan.id === 'free' && isSubscriptionActive
                      ? null // Free plan not clickable when there's an active subscription
                      : () => handlePlanSelection(plan.id)
                  }
                  isPopular={plan.isPopular}
                  isCurrent={plan.isCurrent}
                  isYearly={isYearly}
                  isLoading={loadingPlanId === plan.id} // Only show loading for this specific plan
                  showInstallmentPrice={true}
                  intl={intl}
                />
              ))}
            </div>
            <div className={css.continueBtnContainer}>
              <button
                onClick={() =>
                  history.push({
                    pathname:
                      '/l/draft/00000000-0000-0000-0000-000000000000/new/details',
                    state: {
                      listingType: history?.location?.state?.listingType,
                      from: 'newListingpage',
                    },
                  })
                }
                className={classNames(css.toggleOption, css.continueBtn)}
              >
                <FormattedMessage id="SubscriptionSuccessPage.nextText" />
              </button>
            </div>
            <div className={css.studioContact}>
              <FormattedMessage
                id="SubscriptionPlan.connetWithTattooHubText"
                values={{ contactLink: contactLink }}
              />
            </div>
          </div>
        </div>
        {/* Payment Options Modal */}
        <PaymentOptionsModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          selectedPlan={selectedPlan}
          selectedPaymentOption={selectedPaymentOption}
          setSelectedPaymentOption={setSelectedPaymentOption}
          onConfirm={handlePaymentConfirm}
          isLoading={isLoading}
          intl={intl}
          planData={plans.find(p => p.id === selectedPlan)}
          commitmentMonths={totalInstallmentsRequired}
        />

        {/* Proration Modal */}
        <ProrationModal
          isOpen={showProrationModal}
          onClose={() => {
            setShowProrationModal(false);
            setProrationPreview(null);
            setTargetSwitchPlan(null);
          }}
          onConfirm={() => switchSubscription()}
          previewData={prorationPreview}
          targetPlanName={targetSwitchPlan?.planType}
          isLoading={isLoading}
          intl={intl}
          onManageDisableScrolling={onManageDisableScrolling}
        />

        {/* Error Modal */}
        <Modal
          id="SubscriptionPlan.error"
          isOpen={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div className={css.modalContent}>
            <H3 className={css.modalTitle}>{modalContent.title}</H3>
            <p className={css.modalMessage}>{modalContent.message}</p>
            <div className={css.modalActions}>
              <PrimaryButton className={css.modalButton} onClick={() => setShowErrorModal(false)}>
                <FormattedMessage id="SubscriptionPlan.modalOkButton" />
              </PrimaryButton>
            </div>
          </div>
        </Modal>

        {/* Success Modal */}
        <Modal
          id="SubscriptionPlan.success"
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div className={css.modalContent}>
            <H3 className={css.modalTitle}>{modalContent.title}</H3>
            <p className={css.modalMessage}>{modalContent.message}</p>
            <div className={css.modalActions}>
              <PrimaryButton className={css.modalButton} onClick={() => setShowSuccessModal(false)}>
                <FormattedMessage id="SubscriptionPlan.modalOkButton" />
              </PrimaryButton>
            </div>
          </div>
        </Modal>

        {/* Confirmation Modal */}
        <Modal
          id="SubscriptionPlan.confirm"
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div className={css.modalContent}>
            <H3 className={css.modalTitle}>{modalContent.title}</H3>
            <p className={css.modalMessage}>{modalContent.message}</p>
            <div className={css.modalActions}>
              <PrimaryButton
                className={classNames(css.modalButton, css.dangerButton)}
                onClick={modalContent.onConfirm}
                disabled={isLoading}
              >
                {isLoading ? (
                  <FormattedMessage id="SubscriptionPlan.processing" />
                ) : (
                  <FormattedMessage id="SubscriptionPlan.modalConfirmButton" />
                )}
              </PrimaryButton>
              <SecondaryButton
                className={css.modalButton}
                onClick={() => setShowConfirmModal(false)}
              >
                <FormattedMessage id="SubscriptionPlan.modalCancelButton" />
              </SecondaryButton>
            </div>
          </div>
        </Modal>

        <Modal
          id="SubscriptionPlan.signUpForm"
          isOpen={showAuthenticationModal && !isAuthenticated}
          onClose={() => setShowAuthenticationModal(false)}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div className={css.content}>
            {/* {isLogin ? ( */}
            <ProviderLoginForm
              className={css.loginForm}
              onSubmit={handleLoginSubmit}
              inProgress={authInProgress}
              loginOrSignupError={loginOrSignupError}
              showSignUpLink={false}
              setIsLogin={setIsLogin}
            />
            {/* ) : (
                        <SignUpFormDuringTx
                          setIsLogin={setIsLogin}
                          loginOrSignupError={loginOrSignupError}
                          inProgress={authInProgress}
                          onSubmit={handleSignupSubmit}
                          termsAndConditions={
                            <TermsAndConditions
                              onOpenTermsOfService={() => setTosModalOpen(true)}
                              onOpenPrivacyPolicy={() => setPrivacyModalOpen(true)}
                              intl={intl}
                            />
                          }
                        />
                      )} */}
          </div>
        </Modal>
        <Modal
          id="SubscriptionPlan.usertypeError"
          isOpen={showUserTypeError}
          onClose={() => setShowUserTypeError(false)}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div className={css.content}>
            <div className={css.errMsg}>
              {intl.formatMessage({ id: 'LoginForm.customerLoginNotAllowed' })}
            </div>
          </div>
        </Modal>

        <FooterContainer />
      </LayoutSingleColumn>
    </Page>
  );
};

SubscriptionPlanComponent.defaultProps = {
  currentPlan: 'free',
  scrollingDisabled: false,
};

SubscriptionPlanComponent.propTypes = {
  intl: intlShape.isRequired,
  currentPlan: string,
  currentUser: propTypes.currentUser,
  scrollingDisabled: propTypes.bool,
  onManageDisableScrolling: func,
};

const mapStateToProps = state => {
  const { isAuthenticated, signupError, loginError } = state.auth;

  return {
    currentUser: state.user.currentUser,
    scrollingDisabled: isScrollingDisabled(state),
    isAuthenticated,
    authInProgress: authenticationInProgress(state),
    signupError,
    loginError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),

  submitLogin: ({ email, password }) => dispatch(login(email, password)),
  submitSignup: params => dispatch(signup(params)),
});

const SubscriptionPlan = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(SubscriptionPlanComponent);

export default SubscriptionPlan;
